import {
  AttachFile,
  AutoAwesome,
  Business,
  BusinessCenter,
  Close,
  ExpandMore,
  Language,
} from "@mui/icons-material";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import KnowledgeBaseSelect from "../../components/KnowledgeBaseSelect";
import LoadingButton from "../../components/LoadingButton";
import { COMPANY_SIZES, ICP_FIELD_LIMITS, ICP_TYPES, ICP_TYPE_B2B } from "../../constants";
import { Company } from "../../types";
import { hostname, parseError } from "../../utils";

interface IcpGenerateProps {
  open: boolean;
  onClose: () => void;
  companyId: string;
  company: Company;
  getToken: Function;
  eventListener: Function;
  onSuccess: () => void;
}

export default ({
  open,
  onClose,
  companyId,
  company,
  getToken,
  eventListener,
  onSuccess,
}: IcpGenerateProps) => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [formData, setFormData] = useState<any>({
    name: "",
    icpType: ICP_TYPE_B2B,
    // Your company info (to understand what you sell)
    useCompanyInfo: true,
    companyWebsite: "",
    // Target customer info
    targetIndustry: "",
    targetCompanySize: "",
    targetDescription: "",
    targetWebsites: [] as string[],
    targetWebsiteInput: "",
    // Knowledge base
    useKnowledgeBase: false,
    knowledgeBasePath: "/",
  });
  const [expandedPanel, setExpandedPanel] = useState<string | false>(
    "companyPanel"
  );

  const handleExpandPanel =
    (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => {
      setExpandedPanel(isExpanded ? panel : false);
    };

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      // Initialize form with company data
      const initialData: any = {
        name: `${
          company?.name || "Company"
        } ICP - ${new Date().toLocaleDateString()}`,
        icpType: ICP_TYPE_B2B,
        // Your company info (to understand what you sell)
        useCompanyInfo: true,
        companyWebsite: company?.website || "",
        // Target customer info
        targetIndustry: "",
        targetCompanySize: "",
        targetDescription: "",
        targetWebsites: [],
        targetWebsiteInput: "",
        // Knowledge base
        useKnowledgeBase: false,
        knowledgeBasePath: "/",
      };

      setFormData(initialData);
    }
  }, [open, company]);

  const handleInputChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormData({ ...formData, [name]: checked });

    // If toggling useCompanyInfo, populate or clear the company website
    if (name === "useCompanyInfo") {
      if (checked && company) {
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          companyWebsite: company.website || "",
        });
      } else if (!checked) {
        setFormData({
          ...formData,
          useCompanyInfo: checked,
          companyWebsite: "",
        });
      }
    }
  };

  const handleAddTargetWebsite = () => {
    const website = formData.targetWebsiteInput?.trim();
    if (website && !formData.targetWebsites.includes(website)) {
      setFormData({
        ...formData,
        targetWebsites: [...formData.targetWebsites, website],
        targetWebsiteInput: "",
      });
    }
  };

  const handleRemoveTargetWebsite = (websiteToRemove: string) => {
    setFormData({
      ...formData,
      targetWebsites: formData.targetWebsites.filter(
        (w: string) => w !== websiteToRemove
      ),
    });
  };

  const handleTargetWebsiteKeyDown = (
    event: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleAddTargetWebsite();
    }
  };

  const handleSelectChange = (event: any) => {
    const { name, value } = event.target;
    setFormData({ ...formData, [name]: value });
  };

  const generateProfile = async () => {
    if (!formData.name) {
      eventListener({
        type: "SET_ERROR",
        error: "Profile name is required",
      });
      return;
    }

    setGenerating(true);

    try {
      const token = await getToken();

      // Format input data for the generation endpoint
      const inputData = {
        name: formData.name,
        icpType: formData.icpType,
        // Your company info
        useCompanyInfo: formData.useCompanyInfo,
        companyWebsite: formData.companyWebsite,
        // Target customer info
        targetIndustry: formData.targetIndustry,
        targetCompanySize: formData.targetCompanySize,
        targetDescription: formData.targetDescription,
        targetWebsites: formData.targetWebsites,
        // Knowledge base
        useKnowledgeBase: formData.useKnowledgeBase,
        knowledgeBasePath: formData.knowledgeBasePath,
      };

      await axios.post(
        `${hostname}/content/profiles/generate`,
        {
          companyId,
          data: inputData,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );
      // Generation is now running in the background
      eventListener({
        type: "SET_SUCCESS",
        info: "ICP generation started! This may take a few minutes. Check back shortly to see your completed profile.",
      });
      onSuccess();
      onClose();
    } catch (error) {
      console.error("Error generating ICP:", error);
      eventListener({
        type: "SET_ERROR",
        error: parseError(error),
      });
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center" }}>
            <AutoAwesome color="primary" sx={{ mr: 1 }} />
            <Typography variant="h6">
              Generate Ideal Customer Profile with AI
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={onClose}
            aria-label="close"
          >
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 3 }}>
          <AlertTitle>How This Works</AlertTitle>
          {formData.icpType === "b2c"
            ? "Our AI will analyze your company website to understand what you sell, then use target consumer information to create a comprehensive B2C consumer profile."
            : "Our AI will analyze your company website to understand what you sell, then use target company information and example websites to create a comprehensive B2B customer profile."}
        </Alert>

        {loading && <LinearProgress sx={{ mb: 2 }} />}

        <TextField
          name="name"
          label="Profile Name"
          value={formData.name}
          onChange={handleInputChange}
          fullWidth
          required
          margin="normal"
          error={!formData.name}
          inputProps={{ maxLength: ICP_FIELD_LIMITS.name }}
          helperText={
            !formData.name
              ? "Profile name is required"
              : `${formData.name?.length || 0}/${ICP_FIELD_LIMITS.name} characters`
          }
        />

        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>ICP Type</InputLabel>
          <Select
            name="icpType"
            value={formData.icpType}
            label="ICP Type"
            onChange={handleSelectChange}
          >
            {ICP_TYPES.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Your Company Section */}
        <Accordion
          expanded={expandedPanel === "companyPanel"}
          onChange={handleExpandPanel("companyPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Business sx={{ mr: 1 }} />
            <Typography>Your Company</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              We'll analyze your company website to understand what products or
              services you offer. This helps the AI create a more relevant ICP.
            </Typography>

            <FormControlLabel
              control={
                <Switch
                  checked={formData.useCompanyInfo}
                  onChange={handleSwitchChange}
                  name="useCompanyInfo"
                />
              }
              label="Use current company information"
            />

            <Box sx={{ mt: 2 }}>
              <TextField
                name="companyWebsite"
                label="Your Company Website"
                value={formData.companyWebsite}
                onChange={handleInputChange}
                fullWidth
                placeholder="e.g., https://yourcompany.com"
                InputProps={{
                  startAdornment: (
                    <Language sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
                helperText="The AI will analyze this website to understand your products/services"
              />
            </Box>
          </AccordionDetails>
        </Accordion>

        {/* Target Customer Section */}
        <Accordion
          expanded={expandedPanel === "targetPanel"}
          onChange={handleExpandPanel("targetPanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <BusinessCenter sx={{ mr: 1 }} />
            <Typography>
              {formData.icpType === "b2c" ? "Target Consumer Information" : "Target Customer Information"}
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formData.icpType === "b2c"
                ? "Describe your ideal target consumer. You can provide example websites of brands that serve similar consumers."
                : "Describe your ideal target customer. You can also provide example websites of companies that match your ideal customer profile."}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    name="targetIndustry"
                    value={formData.targetIndustry}
                    label={formData.icpType === "b2c" ? "Target Market Segment" : "Target Industry"}
                    onChange={handleInputChange}
                    placeholder={formData.icpType === "b2c"
                      ? "e.g., Health & wellness, Home improvement, Fashion"
                      : "e.g., Technology, Healthcare, Finance"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.industry }}
                    helperText={`${formData.targetIndustry?.length || 0}/${ICP_FIELD_LIMITS.industry}`}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  {formData.icpType === "b2c" ? (
                    <TextField
                      fullWidth
                      name="targetCompanySize"
                      value={formData.targetCompanySize}
                      label="Target Demographics"
                      onChange={handleInputChange}
                      placeholder="e.g., 25-45 years old, urban, middle income"
                      inputProps={{ maxLength: ICP_FIELD_LIMITS.companySize }}
                      helperText={`${formData.targetCompanySize?.length || 0}/${ICP_FIELD_LIMITS.companySize}`}
                    />
                  ) : (
                    <FormControl fullWidth>
                      <InputLabel>Target Company Size</InputLabel>
                      <Select
                        name="targetCompanySize"
                        value={formData.targetCompanySize}
                        label="Target Company Size"
                        onChange={handleSelectChange}
                      >
                        {COMPANY_SIZES.map((size) => (
                          <MenuItem key={size} value={size}>
                            {size}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    name="targetDescription"
                    label={formData.icpType === "b2c" ? "Target Consumer Description" : "Target Customer Description"}
                    value={formData.targetDescription}
                    onChange={handleInputChange}
                    multiline
                    rows={3}
                    fullWidth
                    placeholder={formData.icpType === "b2c"
                      ? "Describe the ideal consumer you want to target (e.g., 'Health-conscious millennials who value convenience and sustainability')"
                      : "Describe the ideal company you want to target (e.g., 'Mid-size B2B SaaS companies struggling with customer retention')"}
                    inputProps={{ maxLength: ICP_FIELD_LIMITS.description }}
                    helperText={`${formData.targetDescription?.length || 0}/${ICP_FIELD_LIMITS.description}`}
                  />
                </Grid>
              </Grid>
            </Box>

            {/* Target Company/Consumer Websites */}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>
              {formData.icpType === "b2c"
                ? "Example Brand Websites (Optional)"
                : "Example Target Company Websites (Optional)"}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {formData.icpType === "b2c"
                ? "Add websites of brands that serve similar consumers. The AI will analyze these to better understand your target profile."
                : "Add websites of companies that represent your ideal customer. The AI will analyze these to better understand your target profile."}
            </Typography>

            <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
              <TextField
                name="targetWebsiteInput"
                label={formData.icpType === "b2c" ? "Add Brand Website" : "Add Target Company Website"}
                value={formData.targetWebsiteInput}
                onChange={handleInputChange}
                onKeyDown={handleTargetWebsiteKeyDown}
                fullWidth
                placeholder={formData.icpType === "b2c"
                  ? "e.g., https://example-brand.com"
                  : "e.g., https://example-target.com"}
                size="small"
                InputProps={{
                  startAdornment: (
                    <Language sx={{ color: "action.active", mr: 1 }} />
                  ),
                }}
              />
              <Button
                variant="outlined"
                onClick={handleAddTargetWebsite}
                disabled={!formData.targetWebsiteInput?.trim()}
              >
                Add
              </Button>
            </Box>

            {formData.targetWebsites?.length > 0 && (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formData.targetWebsites.map((website: string) => (
                  <Chip
                    key={website}
                    label={website}
                    onDelete={() => handleRemoveTargetWebsite(website)}
                    size="small"
                    variant="outlined"
                  />
                ))}
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

        <Accordion
          expanded={expandedPanel === "knowledgePanel"}
          onChange={handleExpandPanel("knowledgePanel")}
          sx={{ mt: 2 }}
        >
          <AccordionSummary expandIcon={<ExpandMore />}>
            <AttachFile sx={{ mr: 1 }} />
            <Typography>Knowledge Base Documents</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.useKnowledgeBase}
                  onChange={handleSwitchChange}
                  name="useKnowledgeBase"
                />
              }
              label="Include knowledge base documents"
            />

            {formData.useKnowledgeBase && (
              <Box sx={{ mt: 2 }}>
                <KnowledgeBaseSelect
                  getToken={getToken}
                  eventListener={eventListener}
                  company={company}
                  value={formData.knowledgeBasePath}
                  onValueChange={(v) =>
                    setFormData({ ...formData, knowledgeBasePath: v })
                  }
                />
              </Box>
            )}
          </AccordionDetails>
        </Accordion>

      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <LoadingButton
          variant="contained"
          color="primary"
          onClick={generateProfile}
          loading={generating}
          disabled={generating || !formData.name}
          startIcon={<AutoAwesome />}
        >
          {generating ? "Generating..." : "Generate Profile"}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
